import React, { useMemo } from 'react';
import { App } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';

import CloseButton from '../ui/message/CloseButton';
import MessageContent from '../ui/message/MessageContent';

const NOTIFICATION_TYPES = ['success', 'primary', 'secondary', 'warning', 'danger'];
const MESSAGES_TYPES = ['success', 'info', 'error', 'warning', 'loading'];

const DEFAULT_DURATION = 5;
const MESSAGES_ICONS = {
  success: <CheckCircleOutlined />,
  primary: <InfoCircleOutlined />,
  secondary: <InfoCircleOutlined />,
  warning: <InfoCircleOutlined />,
  danger: <WarningOutlined />,
};

/**
 * Displays a message or notification using the provided app instance.
 * @typedef {Function} showMessage
 *
 * @param {Object} options - The options for displaying the message.
 * @param {string} options.content - The content of the message.
 * @param {number} [options.duration] - The duration (in seconds) the message should be displayed. Defaults to a predefined value.
 * @param {Function} [options.callback] - A callback function to execute when the message is closed. Defaults to a no-op function.
 * @param {Array} [options.actions] - An array of actions to display with the message. Defaults to an empty array.
 * @param {boolean} [options.closable=true] - Whether the message can be closed manually.
 * @param {string} [options.position='top'] - The position of the message (e.g., 'top', 'bottom').
 * @param {string} [options.type='primary'] - The type of the message (e.g., 'primary', 'success', 'error').
 * @param {boolean} [options.showProgress] - Whether to show a progress indicator. Defaults to true if `duration` is provided.
 * @param {Object} options.appInstance - The application instance containing `notification` and `message` methods.
 */

const showMessage = ({
  content,
  duration = DEFAULT_DURATION,
  callback = () => {},
  actions = [],
  closable = false,
  position = 'top',
  type = 'primary',
  showProgress = false,
  appInstance,
} = {}) => {
  const { notification } = appInstance;

  notification.open({
    message: null,
    description: (
      <MessageContent
        content={content}
        actions={actions}
        duration={duration}
        showProgress={showProgress}
      />
    ),
    className: `acast-message acast-message__${type}`,
    duration,
    closeIcon: closable && <CloseButton />,
    onClose: callback,
    placement: position,
    icon: MESSAGES_ICONS[type],
    pauseOnHover: false,
    showProgress: false,
  });
};

const showMessageLegacy = ({
  content,
  duration,
  callback = () => {},
  type = 'info',
  appInstance,
} = {}) => {
  const { message } = appInstance;

  if (typeof message[type] !== 'function') {
    type = 'open';
  }

  message[type]({
    content,
    duration: duration || DEFAULT_DURATION,
    onClose: callback,
  });
};

const useMessage = () => {
  const appInstance = App.useApp();

  const messagesHandlers = useMemo(
    () =>
      NOTIFICATION_TYPES.reduce((acc, type) => {
        acc[type] = (messageProps) => {
          showMessage({
            ...messageProps,
            appInstance,
            type,
          });
        };
        return acc;
      }, {}),
    [appInstance]
  );

  const legacyMessagesHandlers = useMemo(
    () =>
      MESSAGES_TYPES.reduce((acc, type) => {
        acc[type] = (messageProps) => {
          showMessageLegacy({
            ...messageProps,
            appInstance,
            type,
          });
        };
        return acc;
      }, {}),
    [appInstance]
  );

  return {
    message: messagesHandlers,
    destroy: appInstance.notification.destroy,
    legacy: {
      message: legacyMessagesHandlers,
      destroy: appInstance.message.destroy,
    },
  };
};

export default useMessage;
